import type { UUID } from '@cmg/common';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItem,
  ListItemText,
  SnackbarManager,
  useDialogState,
} from '@cmg/design-system';
import { FormikAsyncAutocompleteField } from '@cmg/design-system-formik';
import type { FormikConfig } from 'formik';
import { FormikProvider, useFormik } from 'formik';
import defaultTo from 'lodash/defaultTo';
import isEqual from 'lodash/isEqual';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { RelationshipType, RolodexRecordStatus } from '../../../../../../../graphql';
import { refetchFirmsSalesCoverageGridReactiveVar } from '../FirmsSalesCoverageGrid/FirmsSalesCoverageGrid.model';
import { mapEmployeeToOption } from './AddInvestorFirmSalesCoverage.model';
import { useAddInvestorFirmSalesCoverageEmployeeSearch } from './hooks/useAddInvestorFirmSalesCoverageEmployeeSearch';
import { useCreateInvestorFirmSalesCoverageEmployeeRelationship } from './hooks/useCreateInvestorFirmSalesCoverageEmployeeRelationship';

type EmployeeSearchOption = {
  label: string;
  value: string;
  employeeKey: string | null | undefined;
};

const PAGE_SIZE = 25;

export type AddInvestorFirmSalesCoverageProps = Readonly<{
  firmStatus: RolodexRecordStatus;
}>;

export function AddInvestorFirmSalesCoverage({ firmStatus }: AddInvestorFirmSalesCoverageProps) {
  const { crmIntegrationId, firmId = '' } = useParams<{
    crmIntegrationId?: UUID;
    firmId: string;
  }>();
  const { isOpen, onClose, onOpen } = useDialogState();

  const createRelationships =
    useCreateInvestorFirmSalesCoverageEmployeeRelationship(crmIntegrationId);

  const { loadEmployees, loading } =
    useAddInvestorFirmSalesCoverageEmployeeSearch(crmIntegrationId);

  const handleFetchOptions = useCallback(
    async (searchText: string, loadedPage: number = 1) => {
      const { data } = await loadEmployees({
        firmId,
        searchText,
        skip: (loadedPage - 1) * PAGE_SIZE,
        take: PAGE_SIZE,
      });
      return {
        data: (data?.underwriterEmployees?.items ?? []).map(mapEmployeeToOption),
        pagination: {
          hasNext: defaultTo(data?.underwriterEmployees?.pageInfo?.hasNextPage, false),
        },
      };
    },
    [loadEmployees, firmId]
  );

  const handleSubmit = useCallback<
    FormikConfig<{ selectedEmployees: EmployeeSearchOption[] }>['onSubmit']
  >(
    async values => {
      try {
        const inputs = values.selectedEmployees.map(employee => ({
          investorFirmId: firmId,
          relationshipType: RelationshipType.Primary,
          status: RolodexRecordStatus.Effective,
          underwriterEmployeeId: employee.value,
        }));
        await createRelationships(inputs);
        onClose();
        refetchFirmsSalesCoverageGridReactiveVar(true);
        SnackbarManager.success('Sales coverage added successfully');
      } catch {
        SnackbarManager.error('Failed to add sales coverage');
      }
    },
    [createRelationships, onClose, firmId]
  );

  const formik = useFormik<{ selectedEmployees: EmployeeSearchOption[] }>({
    initialValues: {
      selectedEmployees: [],
    },
    onSubmit: handleSubmit,
  });

  const handleSave = useCallback(() => {
    formik.handleSubmit();
  }, [formik]);

  const handleClose = useCallback(() => {
    onClose();
    formik.resetForm();
  }, [onClose, formik]);

  return (
    <FormikProvider value={formik}>
      <Button
        variant="outlined"
        onClick={onOpen}
        disabled={firmStatus === RolodexRecordStatus.Deleted}
      >
        Add Sales Coverage
      </Button>
      <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Add Sales Coverage</DialogTitle>
        <Divider />
        <DialogContent>
          <FormikAsyncAutocompleteField<EmployeeSearchOption, true, true>
            multiple
            name="selectedEmployees"
            isOptionEqualToValue={isEqual}
            isLoadingOptions={loading}
            onFetchOptions={handleFetchOptions}
            label="Employee"
            required
            placeholder="Select Employees..."
            disableClearable
            renderOption={(props, option) => (
              <ListItem {...props}>
                <ListItemText primary={option.label} secondary={option.employeeKey} />
              </ListItem>
            )}
          ></FormikAsyncAutocompleteField>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={handleClose} variant="outlined" color="primary">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </FormikProvider>
  );
}
